import { ClinicalSituation, ConsciousLevel, OxygenScale } from './types';

export const NEWS2_SOURCE_URL =
  'https://www.rcplondon.ac.uk/media/a4ibkkbf/news2-final-report_0_0.pdf';

/**
 * Mokomieji suaugusiųjų ūmios būklės atvejai.
 *
 * Rodikliai pateikiami naudotojui kaip užduotis, bet pasirinkus atvejį į
 * skaičiuoklę automatiškai neįrašomi. Taip naudotojas pats suveda duomenis ir
 * mato, kaip NEWS2 balas apskaičiuojamas iš jo įvesties.
 */
export const CLINICAL_SITUATIONS: ClinicalSituation[] = [
  {
    id: 'acute-presentation',
    title: 'Ūmus atvykimas: karščiavimas ir dusulys',
    summary: '57 metų pacientas atvyksta į skubiosios pagalbos skyrių dėl ūmiai sustiprėjusio dusulio ir karščiavimo.',
    caseDescription:
      'Pacientas sąmoningas, tačiau jaučiasi labai silpnai. Vertinimo metu kvėpavimas padažnėjęs, pulsas dažnas, temperatūra aukšta.',
    vitals: {
      respirationRate: 24,
      oxygenSaturation: 93,
      airOrOxygen: 'Air',
      systolicBP: 106,
      pulseRate: 116,
      consciousness: ConsciousLevel.ALERT,
      temperature: 39.2,
      oxygenScale: OxygenScale.SCALE_1,
    },
    sourceUrl: NEWS2_SOURCE_URL,
  },
  {
    id: 'inpatient-deterioration',
    title: 'Pablogėjimas skyriuje: tachipnėja ir hipotenzija',
    summary: '74 metų stacionaro pacientui per kelias valandas padažnėjo kvėpavimas ir sumažėjo arterinis kraujospūdis.',
    caseDescription:
      'Pacientas kol kas budrus. Palyginti su ankstesniu vertinimu, kvėpavimo dažnis ir pulsas padidėjo, o sistolinis kraujospūdis sumažėjo.',
    vitals: {
      respirationRate: 26,
      oxygenSaturation: 94,
      airOrOxygen: 'Air',
      systolicBP: 89,
      pulseRate: 124,
      consciousness: ConsciousLevel.ALERT,
      temperature: 37.8,
      oxygenScale: OxygenScale.SCALE_1,
    },
    sourceUrl: NEWS2_SOURCE_URL,
  },
  {
    id: 'suspected-infection',
    title: 'Įtariama infekcija: karščiavimas ir tachikardija',
    summary: '68 metų pacientas karščiuoja, jaučia šaltkrėtį ir yra įtariama ūmi infekcija.',
    caseDescription:
      'Pacientas budrus, tačiau atrodo ūmiai sergantis. Reikia įvertinti bendrą būklės sunkumą ir užfiksuoti pradinius rodiklius.',
    vitals: {
      respirationRate: 22,
      oxygenSaturation: 95,
      airOrOxygen: 'Air',
      systolicBP: 102,
      pulseRate: 132,
      consciousness: ConsciousLevel.ALERT,
      temperature: 39.4,
      oxygenScale: OxygenScale.SCALE_1,
    },
    sourceUrl: NEWS2_SOURCE_URL,
  },
  {
    id: 'respiratory-deterioration',
    title: 'Kvėpavimo nepakankamumas su deguonimi',
    summary: '63 metų pacientui dėl patvirtinto hiperkapninio kvėpavimo nepakankamumo paskirta 88–92 % SpO₂ tikslinė saturacija.',
    caseDescription:
      'Pacientas gauna papildomą deguonį. Šiam mokomajam atvejui kompetentingas klinikinis sprendimų priėmėjas pasirinko SpO₂ 2 skalę.',
    vitals: {
      respirationRate: 28,
      oxygenSaturation: 90,
      airOrOxygen: 'Oxygen',
      systolicBP: 118,
      pulseRate: 112,
      consciousness: ConsciousLevel.ALERT,
      temperature: 36.5,
      oxygenScale: OxygenScale.SCALE_2,
    },
    sourceUrl: NEWS2_SOURCE_URL,
  },
  {
    id: 'new-confusion',
    title: 'Naujas sumišimas ir ūmus sąmonės pokytis',
    summary: '82 metų pacientas tapo dezorientuotas ir jo elgesys pasikeitė, palyginti su įprasta būkle.',
    caseDescription:
      'Sumišimas laikomas nauju, kol nepatvirtinta kitaip. Kartu stebimi pakitę kvėpavimo, kraujotakos ir temperatūros rodikliai.',
    vitals: {
      respirationRate: 20,
      oxygenSaturation: 92,
      airOrOxygen: 'Air',
      systolicBP: 108,
      pulseRate: 98,
      consciousness: ConsciousLevel.VPU,
      temperature: 38.6,
      oxygenScale: OxygenScale.SCALE_1,
    },
    sourceUrl: NEWS2_SOURCE_URL,
  },
  {
    id: 'ongoing-monitoring',
    title: 'Pakartotinis vertinimas po intervencijos',
    summary: 'Po pradinio gydymo ūmiai sergančiam pacientui reikia pakartoti gyvybinių rodiklių vertinimą.',
    caseDescription:
      'Pacientas jaučiasi kiek geriau, tačiau kvėpavimo ir kraujotakos rodikliai vis dar pakitę. Reikia įvertinti tendenciją ir NEWS2 rezultatą.',
    vitals: {
      respirationRate: 22,
      oxygenSaturation: 94,
      airOrOxygen: 'Air',
      systolicBP: 105,
      pulseRate: 96,
      consciousness: ConsciousLevel.ALERT,
      temperature: 38.4,
      oxygenScale: OxygenScale.SCALE_1,
    },
    sourceUrl: NEWS2_SOURCE_URL,
  },
];

export const getSituationById = (
  id: ClinicalSituation['id'] | null,
): ClinicalSituation | undefined => {
  if (!id) return undefined;
  return CLINICAL_SITUATIONS.find((situation) => situation.id === id);
};
